import { getAppRouter } from '@/services/app-router'
import { useAccountStore } from '@/stores/account-store'
import { setSessionEnding } from '@/services/auth/session-ending'
import { rememberPostLoginPath } from '@/storage/post-login-redirect'

/**
 * Shared 401-refresh mutex. Coalesces concurrent refresh attempts across
 * all callers (RTK Query base-query + workspace-api + layout-api).
 */
let refreshPromise: Promise<boolean> | null = null

let loggingOut = false
// Set once forceLogout's logout() has cleared the session. A session seen after
// that is a NEW sign-in, which re-arms forceLogout (see rearmAfterNewSession).
let loggedOut = false

// Backend returns this dedicated status when a newer login superseded this
// session. 432 is IANA-unassigned with no vendor/proxy convention, so it can't
// collide with a real error; the client keys on the status alone (no body/header
// parsing, survives CORS/proxies). Must match edge_auth SESSION_SUPERSEDED_STATUS.
const SESSION_SUPERSEDED_STATUS = 432
const RATE_LIMITED_STATUS = 429
// The API's per-user limiter answers 429 with Retry-After of a second or two.
// One retry after that delay makes a limit invisible in the normal case (the
// bucket refills inside the window); a cap keeps a misbehaving header from
// stalling a request, and exactly one attempt keeps a limited client from
// turning into a storm.
const RETRY_AFTER_DEFAULT_MS = 1000
const RETRY_AFTER_MAX_MS = 3000

// The identity resolver fails CLOSED: a missing edge-identity row answers 503 with this
// detail rather than guessing an identity. The session is valid, so a refresh cannot fix
// it — every strict call will keep 503ing. Full logout is the only exit.
// Must match edge_auth.fastapi's HTTPException(503, detail="IDENTITY_UNRESOLVED").
const SERVICE_UNAVAILABLE_STATUS = 503
const IDENTITY_UNRESOLVED = 'IDENTITY_UNRESOLVED'

/**
 * True only for the identity-resolver 503, never a plain outage.
 *
 * Reads a CLONE: the caller still owns the body. A 503 from an ALB or proxy is HTML, and a
 * real outage carries a different detail, so both parse paths have to fail closed to
 * "not this" — logging a user out because a service blinked would be worse than the bug
 * this fixes.
 */
async function isIdentityUnresolved(res: Response): Promise<boolean> {
  if (res.status !== SERVICE_UNAVAILABLE_STATUS) return false
  try {
    const body: unknown = await res.clone().json()
    if (typeof body !== 'object' || body === null) return false
    // edge-user-api envelopes as {error:{message}}; companion/script send a bare {detail}.
    const { error, detail } = body as { error?: { message?: unknown }; detail?: unknown }
    return error?.message === IDENTITY_UNRESOLVED || detail === IDENTITY_UNRESOLVED
  } catch {
    return false
  }
}

function retryAfterMs(header: string | null): number {
  const seconds = Number(header)
  if (!header || !Number.isFinite(seconds) || seconds <= 0) return RETRY_AFTER_DEFAULT_MS
  return Math.min(seconds * 1000, RETRY_AFTER_MAX_MS)
}

/** Resolve after `ms`, or immediately if the signal aborts first. */
function sleep(ms: number, signal?: AbortSignal | null): Promise<void> {
  return new Promise((resolve) => {
    if (signal?.aborted) return resolve()
    const timer = setTimeout(done, ms)
    function done() {
      signal?.removeEventListener('abort', done)
      clearTimeout(timer)
      resolve()
    }
    signal?.addEventListener('abort', done, { once: true })
  })
}

type LogoutReason = 'unrecoverable' | 'superseded' | 'identity'

// A message per reason, so the user is never silently ejected without knowing why. It is
// shown on the /login page (the store's loginMessage) rather than as a toast: a toast lasted
// 8 s and could be replaced by an in-flight request's error toast on the way out.
// 'unrecoverable' stays quiet: a dead refresh token means the session simply expired, which
// the /login page already explains.
const LOGOUT_MESSAGES: Partial<Record<LogoutReason, string>> = {
  superseded: 'You were signed out because your account was signed in on another device.',
  identity: "We couldn't load your account. Please sign in again.",
}

// Fully log out and soft-redirect to /login via the data router, leaving the reason on the
// login page.
async function forceLogout(reason: LogoutReason): Promise<void> {
  if (loggingOut) return
  loggingOut = true
  setSessionEnding(true)
  try {
    await useAccountStore.getState().logout()
  } catch {
    // navigate regardless
  }
  loggedOut = true
  rememberPostLoginPath(
    `${window.location.pathname}${window.location.search}${window.location.hash}`,
  )
  const message = LOGOUT_MESSAGES[reason]
  if (message) useAccountStore.getState().setLoginMessage(message, 'warning')
  const router = getAppRouter()
  if (router) void router.navigate('/login', { replace: true })
  else window.location.assign('/login')
}

// /login is a soft navigation, so this module outlives the sign-out. Once the user signs in
// again, a later forced sign-out must work too.
function rearmAfterNewSession(): void {
  if (loggedOut && useAccountStore.getState().session) {
    loggingOut = false
    loggedOut = false
    setSessionEnding(false)
  }
}

export interface AuthFetchOptions extends RequestInit {
  /**
   * Token to attach as the Bearer Authorization header.
   * - 'access' (default): use the access token.
   * - 'id': use the id token (a few endpoints authenticate with it).
   * - 'none': send no Authorization header (public endpoints like the proxy).
   */
  tokenType?: 'access' | 'id' | 'none'
}

/**
 * `fetch` with automatic Authorization header injection and one-shot
 * 401-refresh-then-retry. Concurrent 401s coalesce to a single refresh
 * via a module-scoped mutex.
 *
 * Auth-refresh logic was originally embedded in `createAuthBaseQuery`
 * (services/api/base-query.ts) and only available to RTK Query slices.
 * This standalone version lets non-RTKQ callers (workspace-api,
 * layout-api) share the same refresh-on-401 behavior.
 *
 * No circuit-breaker counter — that was deliberately removed in a prior
 * refactor (see commit 5c48a8b). The mutex bounds attempts; that's
 * sufficient.
 *
 * Internal use only — feature code calls an ApiClient (get/post/stream/beacon)
 * or RTK Query instead. ESLint blocks direct imports outside services/api.
 */
/**
 * Our headers with the caller's laid over them, one key per header whatever its casing.
 * Spreading a `Headers` instance gives `{}`, and the base query passes one, so its headers
 * used to be silently dropped; spreading both spellings of a name would send it twice
 * (fetch joins them: "application/json, application/json"). A plain object's casing is kept.
 */
function mergeHeaders(
  ours: Record<string, string>,
  caller: HeadersInit | undefined,
): Record<string, string> {
  const merged = { ...ours }
  const entries = !caller
    ? []
    : caller instanceof Headers || Array.isArray(caller)
      ? [...new Headers(caller).entries()]
      : Object.entries(caller)
  for (const [name, value] of entries) {
    for (const key of Object.keys(merged)) {
      if (key.toLowerCase() === name.toLowerCase()) delete merged[key]
    }
    merged[name] = value
  }
  return merged
}

export async function authFetchWithRefresh(
  url: string,
  options: AuthFetchOptions = {},
): Promise<Response> {
  const { tokenType = 'access', ...fetchInit } = options
  rearmAfterNewSession()

  function doFetch(): Promise<Response> {
    const tokens = useAccountStore.getState().session?.tokens
    const token =
      tokenType === 'access'
        ? (tokens?.accessToken ?? null)
        : tokenType === 'id'
          ? (tokens?.idToken ?? null)
          : null
    return fetch(url, {
      ...fetchInit,
      headers: mergeHeaders(
        {
          // Only set Content-Type when there's a body — on bodyless GETs it is
          // a non-simple CORS header that forces a needless OPTIONS preflight.
          ...(fetchInit.body != null ? { 'Content-Type': 'application/json' } : {}),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        fetchInit.headers,
      ),
    })
  }

  let res = await doFetch()

  // 'none' tokenType skips refresh — public endpoints have no session to refresh.
  if (res.status === 401 && tokenType !== 'none') {
    if (!refreshPromise) {
      // Refresh via the store action so the reactive session is updated too — never the
      // bare authService.refreshSession(), which leaves the store stale.
      refreshPromise = useAccountStore
        .getState()
        .refreshSession()
        .finally(() => {
          refreshPromise = null
        })
    }
    const refreshed = await refreshPromise
    if (refreshed) {
      res = await doFetch()
      // A freshly refreshed token still rejected → the session is unrecoverable.
      if (res.status === 401) await forceLogout('unrecoverable')
    } else {
      // The refresh itself failed → the refresh token is dead.
      await forceLogout('unrecoverable')
    }
  }

  if (res.status === RATE_LIMITED_STATUS && !fetchInit.signal?.aborted) {
    await sleep(retryAfterMs(res.headers.get('retry-after')), fetchInit.signal)
    if (!fetchInit.signal?.aborted) res = await doFetch()
  }

  if (res.status === SESSION_SUPERSEDED_STATUS) {
    await forceLogout('superseded')
  }

  // Skipped for 'none': a public endpoint has no session to invalidate, and its 503 says
  // nothing about this user's identity.
  if (tokenType !== 'none' && (await isIdentityUnresolved(res))) {
    await forceLogout('identity')
  }

  return res
}

/** Test-only: reset the in-flight refresh slot. */
export function __resetAuthFetchStateForTests(): void {
  refreshPromise = null
  loggingOut = false
  loggedOut = false
  setSessionEnding(false)
}
